/**
 * InvoicesSummaryCards (11F.7) — the Invoiced / Collected / Due row above
 * the Invoices list (ref `Invoices.png` for which figures exist; the visual
 * treatment is Candeza's own per D13).
 *
 * Figures come from `GET /v1/invoices/summary`, which aggregates the same
 * invoice documents the table lists — so a card can never disagree with the
 * rows beneath it. Drafts and voids are excluded server-side (neither is a
 * receivable).
 *
 * Design notes:
 *  - Semantic colour only where it carries meaning: Due is warm when money
 *    is outstanding and neutral at zero. Invoiced is deliberately neutral —
 *    it is a volume figure, not a good/bad signal.
 *  - `tabular-nums` so the three amounts align on their decimal points.
 *  - Loading renders a shimmer of the real layout rather than a spinner, so
 *    the row doesn't reflow when data lands.
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { HelpTip } from '@/components/entity';
import { formatMinorAmount } from '../config/ui';
import type { InvoicesSummaryResponse } from '../api/billing';

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const Card = styled.div<{ $accent: string }>`
  position: relative;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  overflow: hidden;
  transition: box-shadow ${({ theme }) => theme.transitions.fast},
    transform ${({ theme }) => theme.transitions.fast};

  /* A hairline accent rail encodes which figure this is at a glance,
     without spending saturated colour on the whole card. */
  &::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 3px;
    background: ${({ $accent }) => $accent};
  }

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-1px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.wide};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const Amount = styled.div<{ $accent?: string }>`
  margin-top: 6px;
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  font-variant-numeric: tabular-nums;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.tight};
  color: ${({ theme, $accent }) => $accent ?? theme.colors.text};
  line-height: ${({ theme }) => theme.typography.lineHeights.tight};
`;

const Count = styled.div`
  margin-top: 2px;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const shimmer = keyframes`
  0% { opacity: 0.45; }
  50% { opacity: 0.8; }
  100% { opacity: 0.45; }
`;

const Skeleton = styled.div`
  height: 28px;
  margin-top: 6px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

interface Props {
  data: InvoicesSummaryResponse | undefined;
  isLoading?: boolean;
  isError?: boolean;
  currency?: string;
}

export const InvoicesSummaryCards: React.FC<Props> = ({
  data,
  isLoading,
  isError,
  currency = 'INR',
}) => {
  // A failed summary must not take the table down with it — the list below
  // is the load-bearing surface. Render nothing and let the table speak.
  if (isError) return null;

  const cards = [
    {
      key: 'invoiced',
      label: 'Invoiced',
      helpKey: 'invoices.summary.invoiced',
      accent: '#94a3b8',
      bucket: data?.invoiced,
      amountColor: undefined as string | undefined,
      noun: 'invoice',
    },
    {
      key: 'collected',
      label: 'Collected',
      helpKey: 'invoices.summary.collected',
      accent: '#16a34a',
      bucket: data?.collected,
      amountColor: '#15803d',
      noun: 'invoice',
    },
    {
      key: 'due',
      label: 'Due',
      helpKey: 'invoices.summary.due',
      accent: '#f59e0b',
      bucket: data?.due,
      // Neutral at zero — "₹0.00 due" is good news, not a warning.
      amountColor: (data?.due?.amount ?? 0) > 0 ? '#b45309' : undefined,
      noun: 'unpaid invoice',
    },
  ];

  return (
    <Row data-testid="candeza-invoices-summary">
      {cards.map((c) => (
        <Card key={c.key} $accent={c.accent} data-testid={`candeza-invoices-summary-${c.key}`}>
          <Label>
            {c.label}
            <HelpTip tipKey={c.helpKey} />
          </Label>
          {isLoading || !c.bucket ? (
            <Skeleton />
          ) : (
            <>
              <Amount $accent={c.amountColor}>
                {formatMinorAmount(c.bucket.amount, currency)}
              </Amount>
              <Count>
                {c.bucket.count.toLocaleString('en-IN')} {c.noun}
                {c.bucket.count === 1 ? '' : 's'}
              </Count>
            </>
          )}
        </Card>
      ))}
    </Row>
  );
};

export default InvoicesSummaryCards;
